// The contractor's own papers (Task U): where they stand with Lotus Living, what their trade needs, and uploading a
// renewal — it waits for Lotus Living to check it before it counts.
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Upload } from 'lucide-react';
import { PageHeader, Card, Button } from '../components/ui';

import { contractorApi, type ComplianceKind } from '../lib/maintenance';
import { ComplianceForm } from '../components/maintenance/ComplianceForm';
import { RecordRow, SummaryStrip } from '../components/maintenance/ComplianceCard';

export default function ContractorDocumentsPage() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['contractor-compliance'], queryFn: contractorApi.mine });
  const [adding, setAdding] = useState<ComplianceKind | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const submit = useMutation({
    mutationFn: contractorApi.submit,
    onSuccess: () => { setAdding(null); setError(null); setSent(true); void qc.invalidateQueries({ queryKey: ['contractor-compliance'] }); },
    onError: (e: Error) => setError(e.message),
  });
  const d = q.data;
  const needs = d?.summary.kinds.filter((k) => k.state !== 'OK') ?? [];

  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader title="My documents"
        menu={[{ label: 'Upload a renewal', icon: 'Upload', can: 'contractorPortal.create', onClick: () => setAdding('PUBLIC_LIABILITY') }]} />
      {q.isError && <Card><p className="p-4 text-[12.5px] text-ink-500">{(q.error as Error).message}</p></Card>}
      {d && (
        <div className="space-y-2.5">
          <Card title="Where you stand">
            <SummaryStrip summary={d.summary} />
            {d.summary.blocking && <p className="mt-3 rounded-lg border border-red-100 bg-red-50 px-3 py-2 text-[12.5px] text-red-700">Until these are in date we cannot send you new work without a manager's say-so. Upload the renewal below.</p>}
            {needs.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">{needs.map((k) => (
                <Button key={k.kind + k.label} size="sm" variant="secondary" onClick={() => setAdding(k.kind)}><Upload className="h-3.5 w-3.5" /> {k.label}</Button>))}</div>
            )}
            <p className="mt-3 text-[12px] text-ink-500"><Link to="/app/property?sub=works" className="text-bronze-600 hover:underline">Your jobs →</Link></p>
          </Card>
          {sent && !adding && <p className="rounded-lg border border-emerald-100 bg-emerald-50 px-3 py-2 text-[12.5px] text-emerald-800">Thank you — it is with Lotus Living to check. It counts once they have.</p>}
          {adding && <ComplianceForm key={adding} initialKind={adding} fileRequired submitLabel="Send for checking" pending={submit.isPending} error={error} onCancel={() => setAdding(null)} onSubmit={(i) => submit.mutate(i)} />}
          <Card title="On file">
            {!d.records.length ? <p className="text-[13px] text-ink-400">Nothing yet.</p> : <ul className="divide-y divide-ink-100">{d.records.map((r) => <RecordRow key={r.id} r={r} />)}</ul>}
          </Card>
        </div>
      )}
    </div>
  );
}
